import { computed } from "vue";
import { useOrganizationsQuery } from "@/services/organization";
import {
  resolveActiveOrganizationId,
  useProfileQuery,
  useUpdateSelectedOrganization,
} from "@/services/user";
import { useDataFiltersStore } from "@/store/data_filters";

export function useActiveOrganization() {
  const profile = useProfileQuery();
  const organizations = useOrganizationsQuery();
  const dataFiltersStore = useDataFiltersStore();
  const updateSelectedOrganization = useUpdateSelectedOrganization();

  const activeOrganizationId = computed(() =>
    profile.data ? resolveActiveOrganizationId(profile.data) : undefined,
  );
  const currentOrganization = computed(() =>
    organizations.data?.find((org) => org.id === activeOrganizationId.value),
  );

  const canSwitch = computed(() => (organizations.data?.length ?? 0) > 1);

  // Filters reset before the switch, not after it settles: the switch restarts
  // org-scoped queries, which would otherwise refetch the new org with the old
  // org's resource selection. Callers return the promise from their handler so
  // a failed switch reaches Vue's error handler instead of going unhandled.
  function selectOrganization(orgId: number): Promise<void> {
    if (orgId === currentOrganization.value?.id) return Promise.resolve();
    dataFiltersStore.resetSharableFilters();
    return updateSelectedOrganization(orgId);
  }

  return {
    organizations,
    primaryOrganizationId: computed(() => profile.data?.organization),
    currentOrganization,
    canSwitch,
    selectOrganization,
  };
}
